import { Column, Row, Txt } from '@stenajs-webui/core';
import {
  type MediumIcon,
  stenaCheckCircle,
  stenaExclamationTriangle,
  stenaInfoCircle,
} from '@stenajs-webui/elements';
import type { CssPropColor } from '@stenajs-webui/theme';
import type { FC } from 'react';
import { exhaustSwitchCaseElseThrow } from '../../../common/utils/ExhaustSwitchCase';
import { pluralize } from '../../../common/utils/pluralization';
import { InformationMessageType } from '../../../common-ui/information-message/informationMessageUtils';
import type { NotificationResult } from '../../../services/notifications/notificationsActions';
import { IntermodalJourneyTxt } from './IntermodalJourneyTxt';
import { IntermodalLink } from './IntermodalLink';
import { SailingLink } from './SailingLink';
import { ShowDetailsLink } from './ShowDetailsLink';

export interface BookingResultHeaderProps {
  icon: MediumIcon;
  iconBackgroundColor: CssPropColor;
  title: string;
}

export const getUrgentMessageIconProps = (): Pick<
  BookingResultHeaderProps,
  'icon' | 'iconBackgroundColor'
> => ({
  icon: stenaInfoCircle,
  iconBackgroundColor: '--lhds-color-ui-50',
});

const getIconProps = (type: InformationMessageType): Pick<BookingResultHeaderProps, 'icon'> => {
  switch (type) {
    case InformationMessageType.Confirmed:
      return { icon: stenaCheckCircle };
    case InformationMessageType.HardWarning:
    case InformationMessageType.SoftWarning:
      return { icon: stenaExclamationTriangle };
    case InformationMessageType.RegularInfo:
      return { icon: stenaInfoCircle };
    default:
      return { icon: stenaCheckCircle };
  }
};

export const getBookingResultHeaderProps = (
  result: NotificationResult,
): BookingResultHeaderProps => {
  switch (result.type) {
    case 'waitlist': {
      const count = result.bookings.length;
      return {
        ...getIconProps(InformationMessageType.SoftWarning),
        title: `${count} ${pluralize(count, 'booking on waitlist', 'bookings on waitlist')}`,
        iconBackgroundColor: '--lhds-color-orange-100',
      };
    }
    case 'confirmed': {
      const count = result.bookings.length;
      return {
        ...getIconProps(InformationMessageType.Confirmed),
        title: `${count} ${pluralize(count, 'booking confirmed', 'bookings confirmed')}`,
        iconBackgroundColor: '--lhds-color-green-100',
      };
    }
    case 'create-fail': {
      const count = result.count;
      return {
        ...getIconProps(InformationMessageType.HardWarning),
        title: `${count} ${pluralize(count, 'booking not added', 'bookings not added')}`,
        iconBackgroundColor: '--lhds-color-red-100',
      };
    }
    case 'cancel-fail':
    case 'intermodal-cancel-fail':
      return {
        ...getIconProps(InformationMessageType.HardWarning),
        title: 'Cancellation failed',
        iconBackgroundColor: '--lhds-color-red-100',
      };
    case 'cancel-success':
    case 'intermodal-cancel-success':
      return {
        ...getIconProps(InformationMessageType.Confirmed),
        title: 'Booking cancelled',
        iconBackgroundColor: '--lhds-color-ui-200',
      };
    case 'switch-success':
      return {
        ...getIconProps(InformationMessageType.Confirmed),
        title: 'Switch successful',
        iconBackgroundColor: '--lhds-color-green-100',
      };
    case 'switch-fail':
      return {
        ...getIconProps(InformationMessageType.HardWarning),
        title: 'Switch failed',
        iconBackgroundColor: '--lhds-color-red-100',
      };
    case 'intermodal-created':
      return {
        ...getIconProps(InformationMessageType.Confirmed),
        title: 'Booking request created',
        iconBackgroundColor: '--lhds-color-green-100',
      };
    default:
      return exhaustSwitchCaseElseThrow(result);
  }
};

export const BookingResultContent: FC<{ result: NotificationResult }> = ({ result }) => {
  const content = getResultContent(result);
  const allMessages = getResultMessages(result);

  return (
    <Column gap={2}>
      {content}
      {allMessages.length > 0 && (
        <Column>
          {allMessages.map((message, index) => (
            <Txt key={index}>{message}</Txt>
          ))}
        </Column>
      )}
    </Column>
  );
};

const getResultMessages = (result: NotificationResult): string[] => [
  ...('messages' in result ? result.messages : []),
  ...('warnings' in result ? result.warnings : []),
  ...('errors' in result ? result.errors : []),
];

const getResultContent = (result: NotificationResult) => {
  switch (result.type) {
    case 'waitlist': {
      const count = result.bookings.length;
      return (
        <>
          {count > 1 && <Txt>{count} bookings were added to waitlist</Txt>}
          {result.sailing && (
            <Row>
              <SailingLink sailing={result.sailing} />
            </Row>
          )}
          <BookingLinks bookings={result.bookings} />
        </>
      );
    }
    case 'confirmed': {
      const count = result.bookings.length;
      return (
        <>
          {count > 1 && <Txt>{count} bookings were confirmed</Txt>}
          {result.sailing && (
            <Row>
              <SailingLink sailing={result.sailing} />
            </Row>
          )}
          <BookingLinks bookings={result.bookings} />
        </>
      );
    }
    case 'create-fail': {
      const count = result.count;
      return (
        <>
          {count > 1 && <Txt>{count} bookings were not added</Txt>}
          {result.sailing && (
            <Row>
              <SailingLink sailing={result.sailing} />
            </Row>
          )}
        </>
      );
    }
    case 'cancel-fail':
      return (
        <Txt>
          Booking <ShowDetailsLink bookingNo={result.bookingNo} /> could not be cancelled
        </Txt>
      );
    case 'cancel-success':
      return (
        <Txt>
          Booking <ShowDetailsLink bookingNo={result.bookingNo} /> was cancelled successfully
        </Txt>
      );
    case 'intermodal-cancel-fail':
      return (
        <Txt>
          Intermodal booking <IntermodalLink bookingNo={result.bookingNo} /> could not be cancelled
        </Txt>
      );
    case 'intermodal-cancel-success':
      return (
        <Txt>
          Intermodal booking <IntermodalLink bookingNo={result.bookingNo} /> was cancelled
          successfully
        </Txt>
      );
    case 'switch-success':
      return (
        <>
          {result.sailing && (
            <Row>
              <SailingLink sailing={result.sailing} />
            </Row>
          )}
          <Column>
            <Txt>
              Booking{' '}
              <ShowDetailsLink
                bookingNo={result.waitlistedBookingNo}
                action="Show booking details (switch result)"
              />{' '}
              is now Confirmed.
            </Txt>
            <Txt>
              Booking{' '}
              <ShowDetailsLink
                bookingNo={result.confirmedBookingNo}
                action="Show booking details (switch result)"
              />{' '}
              is now Waitlisted.
            </Txt>
          </Column>
        </>
      );
    case 'switch-fail':
      return null;
    case 'intermodal-created':
      return (
        <Column gap={2}>
          <IntermodalJourneyTxt booking={result.booking} />
          <Row gap>
            <IntermodalLink bookingNo={result.booking.bookingNo} />
            <Txt>{result.booking.vehicleReg || result.booking.iluCode || ''}</Txt>
          </Row>
        </Column>
      );
    default:
      return exhaustSwitchCaseElseThrow(result);
  }
};

const BookingLinks = ({
  bookings,
}: {
  bookings: { bookingNo: number; vehicleRegNo?: string | null; trailerRegNo?: string | null }[];
}) =>
  bookings.length > 0 ? (
    <Column gap={0.5}>
      {bookings.map(booking => (
        <Row key={booking.bookingNo} gap>
          <ShowDetailsLink bookingNo={booking.bookingNo} />
          <Txt>{booking.vehicleRegNo || booking.trailerRegNo || ''}</Txt>
        </Row>
      ))}
    </Column>
  ) : null;
